import { Add as AddIcon, Assignment as EmptyIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon, Groups as GroupsIcon, Search as SearchIcon, Subject as SubjectIcon, Warning as WarningIcon } from "@mui/icons-material"
import { Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Pagination, Paper, Select, Stack, TextField, Typography } from "@mui/material"
import axios from "axios"
import { useEffect, useState } from "react"

import BatchPicker from "./BatchPicker"

export default function HomeworkAssignments({ snackbar }) {
	const apiUrl = process.env.REACT_APP_API

	// ─── Filter option lists ────────────────────────────────────────────────
	const [batches, setBatches] = useState([])
	const [subjects, setSubjects] = useState([])

	useEffect(() => {
		axios
			.get(`${apiUrl}/franchise/batch/getall?status=active&limit=100`)
			.then((res) => setBatches(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setBatches([]))
		axios
			.get(`${apiUrl}/franchise/homework/subject/getall?status=active&limit=100`)
			.then((res) => setSubjects(res.data.status === true ? res.data.data || [] : []))
			.catch(() => setSubjects([]))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// ─── List State ───────────────────────────────────────────────────────────
	const [assignments, setAssignments] = useState([])
	const [loading, setLoading] = useState(true)
	const [searchInput, setSearchInput] = useState("")
	const [search, setSearch] = useState("")
	const [statusFilter, setStatusFilter] = useState("all")
	const [batchFilter, setBatchFilter] = useState("all")
	const [subjectFilter, setSubjectFilter] = useState("all")
	const [page, setPage] = useState(1)
	const [pagination, setPagination] = useState({
		current_page: 1,
		limit: 10,
		total_records: 0,
		total_pages: 1,
	})
	const [stats, setStats] = useState({ active: 0, closed: 0, total: 0 })

	// ─── Form / Dialog State ──────────────────────────────────────────────────
	const emptyForm = { batch_id: "", subjectid: "", hw_title: "", hw_description: "", hw_due_date: "", hw_status: "active" }
	const [open, setOpen] = useState("") // "" | "add" | "edit" | "delete"
	const [action, setAction] = useState("") // "" | "add" | "update" | "delete"
	const [form, setForm] = useState(emptyForm)
	const [selectedAssignment, setSelectedAssignment] = useState(null)

	// ─── Fetch ────────────────────────────────────────────────────────────────
	const getAssignments = () => {
		setLoading(true)
		const params = new URLSearchParams()
		if (search) params.append("search", search)
		if (statusFilter !== "all") params.append("status", statusFilter)
		if (batchFilter !== "all") params.append("batch_id", batchFilter)
		if (subjectFilter !== "all") params.append("subjectid", subjectFilter)
		params.append("page", page)

		axios
			.get(`${apiUrl}/franchise/homework/getall?${params.toString()}`)
			.then((res) => {
				if (res.data.status === true) {
					setAssignments(res.data.data || [])
					if (res.data.pagination) setPagination(res.data.pagination)
					if (res.data.stats) setStats(res.data.stats)
				} else {
					setAssignments([])
				}
			})
			.catch(() => setAssignments([]))
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		getAssignments()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, statusFilter, batchFilter, subjectFilter, page])

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			setSearch(searchInput)
			setPage(1)
		}
	}

	const handlePageChange = (e, value) => {
		setPage(value)
		window.scrollTo({ top: 0, behavior: "smooth" })
	}

	// ─── Add ──────────────────────────────────────────────────────────────────
	const openAdd = () => {
		setForm(emptyForm)
		setOpen("add")
	}

	const handleAdd = () => {
		setAction("add")
		axios
			.post(
				`${apiUrl}/franchise/homework/add`,
				{
					batch_id: form.batch_id,
					subjectid: form.subjectid,
					hw_title: form.hw_title,
					hw_description: form.hw_description,
					hw_due_date: form.hw_due_date,
				},
				{ headers: { "Content-Type": "application/json" } }
			)
			.then((res) => {
				snackbar && snackbar(res.data.message || "Home work created")
				if (res.data.status === true) {
					setOpen("")
					setPage(1)
					getAssignments()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Edit ─────────────────────────────────────────────────────────────────
	const openEdit = (assignment) => {
		setSelectedAssignment(assignment)
		setForm({
			batch_id: assignment.batch_id,
			subjectid: assignment.subjectid,
			hw_title: assignment.hw_title,
			hw_description: assignment.hw_description || "",
			hw_due_date: assignment.hw_due_date || "",
			hw_status: assignment.hw_status,
		})
		setOpen("edit")
	}

	const handleUpdate = () => {
		setAction("update")
		axios
			.post(`${apiUrl}/franchise/homework/update`, { homeworkid: selectedAssignment.homeworkid, ...form }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Home work updated")
				if (res.data.status === true) {
					setOpen("")
					getAssignments()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Delete ───────────────────────────────────────────────────────────────
	const openDelete = (assignment) => {
		setSelectedAssignment(assignment)
		setOpen("delete")
	}

	const handleDelete = () => {
		setAction("delete")
		axios
			.post(`${apiUrl}/franchise/homework/delete`, { homeworkid: selectedAssignment.homeworkid }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Home work deleted")
				if (res.data.status === true) {
					setOpen("")
					getAssignments()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Helpers ──────────────────────────────────────────────────────────────
	const formatDate = (d) => {
		if (!d) return "No due date"
		return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
	}

	const statusMeta = (status) => {
		switch (status) {
			case "active":
				return { label: "Active", color: "success", border: "success.main" }
			default:
				return { label: "Closed", color: "default", border: "grey.400" }
		}
	}

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<Box className="content" pt={3} pb={5}>
			<Container maxWidth="lg">
				{/* ── Page Header ─────────────────────────────────── */}
				<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
					<Box>
						<Typography variant="h5" fontWeight={700}>
							Home Work Assignments
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Assign home work to a batch and subject
						</Typography>
					</Box>
					<Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 2 }}>
						New Assignment
					</Button>
				</Stack>

				{/* ── Summary Strip ───────────────────────────────── */}
				<Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
					{[
						{ key: "all", label: "Total Assignments", value: stats.total, color: "text.primary" },
						{ key: "active", label: "Active", value: stats.active, color: "success.main" },
						{ key: "closed", label: "Closed", value: stats.closed, color: "text.secondary" },
					].map((card) => (
						<Paper
							key={card.key}
							variant="outlined"
							onClick={() => {
								setStatusFilter(card.key)
								setPage(1)
							}}
							sx={{
								px: 2.5,
								py: 1.5,
								borderRadius: 2,
								minWidth: 140,
								cursor: "pointer",
								borderWidth: statusFilter === card.key ? 2 : 1,
								borderColor: statusFilter === card.key ? card.color : "divider",
								transition: "border-color .15s, box-shadow .15s",
								"&:hover": { boxShadow: 2 },
							}}
						>
							<Typography variant="caption" color="text.secondary">
								{card.label}
							</Typography>
							<Typography variant="h6" fontWeight={700} color={card.color}>
								{card.value}
							</Typography>
						</Paper>
					))}
				</Stack>

				{/* ── Filters ─────────────────────────────────────── */}
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
						<TextField
							fullWidth
							size="small"
							placeholder="Search by title..."
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={handleSearchSubmit}
							sx={{ minWidth: 200 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" color="action" />
									</InputAdornment>
								),
							}}
						/>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Batch</InputLabel>
							<Select
								label="Batch"
								value={batchFilter}
								onChange={(e) => {
									setBatchFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Batches</MenuItem>
								{batches.map((b) => (
									<MenuItem key={b.batch_id} value={b.batch_id}>
										{b.batch_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Subject</InputLabel>
							<Select
								label="Subject"
								value={subjectFilter}
								onChange={(e) => {
									setSubjectFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Subjects</MenuItem>
								{subjects.map((s) => (
									<MenuItem key={s.subjectid} value={s.subjectid}>
										{s.subject_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 140 }}>
							<InputLabel>Status</InputLabel>
							<Select
								label="Status"
								value={statusFilter}
								onChange={(e) => {
									setStatusFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Status</MenuItem>
								<MenuItem value="active">Active</MenuItem>
								<MenuItem value="closed">Closed</MenuItem>
							</Select>
						</FormControl>
						<Button
							variant="outlined"
							color="inherit"
							onClick={() => {
								setSearchInput("")
								setSearch("")
								setStatusFilter("all")
								setBatchFilter("all")
								setSubjectFilter("all")
								setPage(1)
							}}
						>
							Clear
						</Button>
					</Stack>
				</Paper>

				{/* ── Assignment Cards ────────────────────────────── */}
				{loading ? (
					<Box textAlign="center" py={8}>
						<CircularProgress />
					</Box>
				) : assignments.length === 0 ? (
					<Paper variant="outlined" sx={{ py: 8, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
						<EmptyIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
						<Typography variant="h6" color="text.secondary">
							No assignments found
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={2}>
							Try adjusting your search, or create a new assignment to get started
						</Typography>
						<Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
							Create Assignment
						</Button>
					</Paper>
				) : (
					<Grid container spacing={2}>
						{assignments.map((row) => (
							<Grid item xs={12} sm={6} md={4} key={row.homeworkid}>
								<Paper
									variant="outlined"
									sx={{
										borderRadius: 3,
										overflow: "hidden",
										height: "100%",
										display: "flex",
										flexDirection: "column",
										borderLeft: "5px solid",
										borderLeftColor: statusMeta(row.hw_status).border,
										transition: "box-shadow .2s, transform .2s",
										"&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
									}}
								>
									<Box p={2} flexGrow={1}>
										<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
											<Typography fontWeight={700} maxWidth={190}>
												{row.hw_title}
											</Typography>
											<Chip size="small" label={statusMeta(row.hw_status).label} color={statusMeta(row.hw_status).color} />
										</Stack>

										{row.hw_description && (
											<Typography variant="body2" color="text.secondary" mb={1} sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
												{row.hw_description}
											</Typography>
										)}

										<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
											<Chip size="small" variant="outlined" icon={<GroupsIcon fontSize="small" />} label={row.batch_name} />
											<Chip size="small" variant="outlined" icon={<SubjectIcon fontSize="small" />} label={row.subject_name} />
										</Stack>

										<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ bgcolor: "action.hover", borderRadius: 2, px: 1.5, py: 1, mt: 1.5 }}>
											<Typography variant="body2" fontWeight={600}>
												Due: {formatDate(row.hw_due_date)}
											</Typography>
											<Chip size="small" color="info" label={`${row.submission_count} submission${row.submission_count === 1 ? "" : "s"}`} />
										</Stack>
									</Box>

									<Divider />
									<Stack direction="row">
										<Button fullWidth size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(row)} sx={{ borderRadius: 0, py: 1 }}>
											Edit
										</Button>
										<Divider orientation="vertical" flexItem />
										<Button fullWidth size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => openDelete(row)} sx={{ borderRadius: 0, py: 1 }}>
											Delete
										</Button>
									</Stack>
								</Paper>
							</Grid>
						))}
					</Grid>
				)}

				{/* ── Pagination ──────────────────────────────────── */}
				{pagination.total_pages > 1 && (
					<Box mt={4} display="flex" justifyContent="center">
						<Pagination count={pagination.total_pages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
					</Box>
				)}
			</Container>

			{/* ── Add / Edit Dialog ───────────────────────────────── */}
			<Dialog open={open === "add" || open === "edit"} fullWidth maxWidth="sm" onClose={() => setOpen("")}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					{open === "add" ? "Create New Assignment" : "Edit Assignment"}
					<IconButton size="small" onClick={() => setOpen("")}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<Divider />
				<DialogContent>
					<Stack spacing={2} mt={1}>
						<Typography variant="subtitle2" color="text.secondary">
							Batch
						</Typography>
						<BatchPicker value={form.batch_id} onChange={(batch_id) => setForm({ ...form, batch_id })} />

						<FormControl fullWidth>
							<InputLabel>Subject</InputLabel>
							<Select label="Subject" value={form.subjectid} onChange={(e) => setForm({ ...form, subjectid: e.target.value })}>
								{subjects.map((s) => (
									<MenuItem key={s.subjectid} value={s.subjectid}>
										{s.subject_name}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<TextField label="Title" fullWidth value={form.hw_title} onChange={(e) => setForm({ ...form, hw_title: e.target.value })} />
						<TextField label="Description" fullWidth multiline minRows={3} value={form.hw_description} onChange={(e) => setForm({ ...form, hw_description: e.target.value })} />
						<TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.hw_due_date} onChange={(e) => setForm({ ...form, hw_due_date: e.target.value })} />

						{open === "edit" && (
							<FormControl fullWidth>
								<InputLabel>Status</InputLabel>
								<Select label="Status" value={form.hw_status} onChange={(e) => setForm({ ...form, hw_status: e.target.value })}>
									<MenuItem value="active">Active</MenuItem>
									<MenuItem value="closed">Closed</MenuItem>
								</Select>
							</FormControl>
						)}
					</Stack>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ p: 2 }}>
					<Button color="inherit" onClick={() => setOpen("")}>
						Cancel
					</Button>
					<Button variant="contained" disabled={action === "add" || action === "update" || !form.batch_id || !form.subjectid || !form.hw_title} onClick={open === "add" ? handleAdd : handleUpdate}>
						{action === "add" || action === "update" ? <CircularProgress size={20} color="inherit" /> : open === "add" ? "Create Assignment" : "Save Changes"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Delete Confirm Dialog ───────────────────────────── */}
			<Dialog open={open === "delete"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
				<DialogContent>
					<Box textAlign="center">
						<WarningIcon sx={{ width: 64, height: 64 }} color="error" />
						<Typography color="error" fontSize={20} fontWeight={600} mt={1}>
							Delete this assignment?
						</Typography>
						<Typography color="text.secondary" fontSize={13} mb={2}>
							<strong>{selectedAssignment?.hw_title}</strong> will be permanently removed.
							{selectedAssignment?.submission_count > 0 ? ` This will also delete ${selectedAssignment.submission_count} student submission${selectedAssignment.submission_count === 1 ? "" : "s"}.` : ""}
						</Typography>
					</Box>
					<Stack spacing={1}>
						<Button variant="contained" color="error" disabled={action === "delete"} onClick={handleDelete}>
							{action === "delete" ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
						</Button>
						<Button variant="outlined" color="inherit" onClick={() => setOpen("")}>
							Cancel
						</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</Box>
	)
}
